import React, { useState } from "react";
import { Chip, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import MemoryIcon from "@material-ui/icons/Memory";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";
import LaunchIcon from "@material-ui/icons/Launch";

import Title from "../components/title";
import { getTranslate } from "../localization";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    padding: "52px 44px 90px",
    direction: theme.direction,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    [theme.breakpoints.down("sm")]: { padding: "34px 22px 64px" },
    [theme.breakpoints.down("xs")]: { padding: "28px 16px 56px" },
  },
  inner: {
    width: "min(100%, 1320px)",
    margin: "0 auto",
  },
  lead: {
    maxWidth: 860,
    marginTop: -15,
    marginBottom: 40,
    color: "#8f9db1",
    fontSize: 13,
    lineHeight: 1.9,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  section: {
    marginBottom: 64,
  },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    marginBottom: 24,
    direction: theme.direction,
  },
  headingIcon: {
    flexShrink: 0,
    color: "#91a0b7",
    fontSize: 30,
  },
  headingText: {
    color: "#f7f9fc",
    fontWeight: 850,
    fontSize: "clamp(1.45rem,2.4vw,2rem)",
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  featuredCard: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 0,
    borderRadius: 22,
    overflow: "hidden",
    background: "linear-gradient(150deg,rgba(12,29,57,.96),rgba(8,17,33,.97))",
    border: "1px solid rgba(22,139,255,.18)",
    boxShadow: "0 16px 38px rgba(0,0,0,.17)",
    transition: "transform .25s ease, border-color .25s ease, box-shadow .25s ease",
    "&:hover": {
      transform: "translateY(-6px)",
      borderColor: "rgba(22,139,255,.40)",
      boxShadow: "0 24px 50px rgba(0,0,0,.24)",
    },
  },
  featuredMedia: {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    minHeight: 190,
    overflow: "hidden",
    background: "linear-gradient(145deg,#0c1a31,#08111f)",
    borderBottom: "1px solid rgba(148,163,184,.09)",
  },
  featuredImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform .4s ease",
    "$featuredCard:hover &": {
      transform: "scale(1.025)",
    },
  },
  featuredFallback: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: 24,
    textAlign: "center",
    color: "#7dbdff",
    background:
      "radial-gradient(circle at 50% 35%,rgba(22,139,255,.13),transparent 42%),linear-gradient(145deg,#0b1830,#07111f)",
  },
  fallbackMark: {
    fontSize: "clamp(1.8rem,4vw,3rem)",
    fontWeight: 900,
    letterSpacing: theme.direction === "rtl" ? 0 : ".06em",
  },
  fallbackDomain: {
    color: "#71839b",
    fontSize: 10,
    letterSpacing: ".07em",
  },
  domainBadge: {
    position: "absolute",
    bottom: 12,
    left: theme.direction === "rtl" ? "auto" : 12,
    right: theme.direction === "rtl" ? 12 : "auto",
    zIndex: 2,
    maxWidth: "calc(100% - 24px)",
    padding: "6px 9px",
    borderRadius: 999,
    color: "#dbeafe",
    fontSize: 9,
    fontWeight: 750,
    background: "rgba(4,10,20,.75)",
    border: "1px solid rgba(148,163,184,.17)",
    backdropFilter: "blur(10px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  featuredBody: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    padding: 22,
    [theme.breakpoints.down("xs")]: { padding: 18 },
  },
  type: {
    alignSelf: theme.direction === "rtl" ? "flex-start" : "flex-start",
    display: "inline-flex",
    maxWidth: "100%",
    padding: "5px 9px",
    marginBottom: 14,
    borderRadius: 999,
    color: "#6bb5ff",
    fontSize: 9,
    fontWeight: 800,
    lineHeight: 1.5,
    letterSpacing: theme.direction === "rtl" ? 0 : ".07em",
    textTransform: theme.direction === "rtl" ? "none" : "uppercase",
    background: "rgba(22,139,255,.07)",
    border: "1px solid rgba(22,139,255,.16)",
  },
  featuredTitle: {
    color: "#f5f8fc",
    fontWeight: 850,
    fontSize: "clamp(1.25rem,2.1vw,1.65rem)",
    lineHeight: 1.48,
    marginBottom: 11,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
  },
  featuredDesc: {
    color: "#95a3b7",
    fontSize: 13,
    lineHeight: 1.92,
    marginBottom: 16,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 16,
  },
  tag: {
    color: "#a7b8cf",
    height: 27,
    fontSize: 9,
    background: "rgba(255,255,255,.02)",
    border: "1px solid rgba(148,163,184,.11)",
  },
  metrics: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    marginTop: "auto",
  },
  metric: {
    padding: "6px 8px",
    borderRadius: 8,
    color: "#78baff",
    fontSize: 9,
    lineHeight: 1.5,
    background: "rgba(22,139,255,.055)",
    border: "1px solid rgba(22,139,255,.12)",
  },
  externalLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    width: "fit-content",
    marginTop: 18,
    color: "#2999ff",
    fontSize: 11,
    fontWeight: 800,
    textDecoration: "none",
    transition: "color .2s ease, transform .2s ease",
    "&:hover": {
      color: "#73bcff",
      transform: theme.direction === "rtl" ? "translateX(-2px)" : "translateX(2px)",
    },
  },
  linkIcon: {
    transform: theme.direction === "rtl" ? "scaleX(-1)" : "none",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 0,
    borderRadius: 19,
    overflow: "hidden",
    background: "linear-gradient(145deg,rgba(14,25,48,.90),rgba(8,16,31,.96))",
    border: "1px solid rgba(102,122,164,.14)",
    boxShadow: "0 13px 32px rgba(0,0,0,.14)",
    transition: "transform .23s ease, border-color .23s ease, box-shadow .23s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      borderColor: "rgba(22,139,255,.29)",
      boxShadow: "0 20px 42px rgba(0,0,0,.21)",
    },
  },
  imageWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    overflow: "hidden",
    background: "#091326",
  },
  image: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform .35s ease",
    "$card:hover &": { transform: "scale(1.03)" },
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    padding: 18,
  },
  cardTitle: {
    color: "#f1f5f9",
    fontWeight: 800,
    marginBottom: 7,
    lineHeight: 1.48,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  cardDesc: {
    color: "#8997ac",
    fontSize: 12,
    lineHeight: 1.8,
    margin: 0,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  cardLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    width: "fit-content",
    marginTop: 14,
    color: "#69b5ff",
    fontSize: 10,
    fontWeight: 800,
  },
}));

function FeaturedProject({ item, classes, visitProject }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(item.image) && !imageFailed;

  return (
    <article className={classes.featuredCard}>
      <div className={classes.featuredMedia}>
        <div className={classes.featuredFallback}>
          <div className={classes.fallbackMark}>{item.visualLabel}</div>
          <div className={classes.fallbackDomain}>{item.domain}</div>
        </div>

        {showImage ? (
          <img
            src={item.image}
            alt={item.imageAlt || item.title}
            className={classes.featuredImage}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : null}

        {item.domain ? <div className={classes.domainBadge}>{item.domain}</div> : null}
      </div>

      <div className={classes.featuredBody}>
        <div className={classes.type}>{item.type}</div>
        <Typography component="h3" className={classes.featuredTitle}>{item.title}</Typography>
        <div className={classes.featuredDesc}>{item.desc}</div>

        <div className={classes.tags}>
          {item.tags.map((tag) => (
            <Chip key={tag} label={tag} variant="outlined" className={classes.tag} />
          ))}
        </div>

        <div className={classes.metrics}>
          {item.metrics.map((metric) => (
            <span key={metric} className={classes.metric}>{metric}</span>
          ))}
        </div>

        {item.href ? (
          <a href={item.href} target="_blank" rel="noopener noreferrer" className={classes.externalLink}>
            <span>{visitProject}</span><LaunchIcon className={classes.linkIcon} fontSize="small" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

function ProjectCard({ item, classes, visitProject }) {
  return (
    <article className={classes.card}>
      <div className={classes.imageWrap}>
        <img src={item.image} alt={item.title} className={classes.image} loading="lazy" />
      </div>
      <div className={classes.cardBody}>
        <Typography component="h3" variant="h5" className={classes.cardTitle}>{item.title}</Typography>
        <p className={classes.cardDesc}>{item.desc}</p>
        {item.href ? (
          <a href={item.href} target="_blank" rel="noopener noreferrer" className={classes.cardLink}>
            <span>{visitProject}</span><LaunchIcon className={classes.linkIcon} fontSize="small" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

export default function PortfolioScreen() {
  const classes = useStyles();
  const translate = getTranslate();

  return (
    <div className={`${classes.root} modern-page`}>
      <div className={classes.inner}>
        <Title title={translate.portfolio} eyebrow={translate.portfolioEyebrow} />
        <div className={classes.lead}>{translate.portfolioLead}</div>

        <section className={classes.section}>
          <div className={classes.heading}>
            <WorkOutlineIcon className={classes.headingIcon} />
            <Typography component="h2" className={classes.headingText}>{translate.featuredWork}</Typography>
          </div>

          <Grid container spacing={3}>
            {translate.featuredProjects.map((item) => (
              <Grid item xs={12} md={12} lg={4} key={item.id}>
                <FeaturedProject item={item} classes={classes} visitProject={translate.visitProject} />
              </Grid>
            ))}
          </Grid>
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <MemoryIcon className={classes.headingIcon} />
            <Typography component="h2" className={classes.headingText}>{translate.selectedAIProjects}</Typography>
          </div>

          <Grid container spacing={3}>
            {translate.portfoliosmlList.map((item) => (
              <Grid item xs={12} md={6} key={item.id}>
                <ProjectCard item={item} classes={classes} visitProject={translate.visitProject} />
              </Grid>
            ))}
          </Grid>
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <CodeOutlinedIcon className={classes.headingIcon} />
            <Typography component="h2" className={classes.headingText}>{translate.frontend}</Typography>
          </div>

          <Grid container spacing={3}>
            {translate.portfoliosList.map((item) => (
              <Grid item xs={12} md={6} lg={4} key={item.id}>
                <ProjectCard item={item} classes={classes} visitProject={translate.visitProject} />
              </Grid>
            ))}
          </Grid>
        </section>
      </div>
    </div>
  );
}
